import { create } from "zustand";
import type { ChatWithLastAttendance, DocID } from "@/types";

export const BULK_SELECTION_CAP = 500;

export type BulkSelectionMode = "off" | "selecting" | "all_in_tab";

export type BulkSelectionState = {
  mode: BulkSelectionMode;
  selectedChatIds: Set<DocID>;
  tabTotal: number | null;
  lastClickedChatId: DocID | null;
  isConfirmModalOpen: boolean;
  isTagModalOpen: boolean;
  isHintVisible: boolean;
  chatsDataCache: Map<DocID, ChatWithLastAttendance>;
};

export type BulkSelectionActions = {
  enterSelectionMode: () => void;
  exitSelectionMode: () => void;
  toggleChat: (chatId: DocID) => void;
  selectAllVisible: (chatIds: DocID[]) => void;
  selectAllInTab: () => void;
  selectFailedChats: (chatIds: DocID[]) => void;
  selectRange: (visibleIds: DocID[], fromId: DocID, toId: DocID) => void;
  clearSelection: () => void;
  setTabTotal: (total: number | null) => void;
  decrementTabTotalBy: (delta: number) => void;
  setLastClickedChatId: (id: DocID | null) => void;
  onFilterChanged: () => void;
  openConfirmModal: () => void;
  closeConfirmModal: () => void;
  openTagModal: () => void;
  closeTagModal: () => void;
  dismissHint: () => void;
  cacheChats: (chats: ChatWithLastAttendance[]) => void;
};

type Store = BulkSelectionState & BulkSelectionActions;

export const useBulkSelectionStore = create<Store>((set) => ({
  mode: "off",
  selectedChatIds: new Set<DocID>(),
  tabTotal: null,
  lastClickedChatId: null,
  isConfirmModalOpen: false,
  isTagModalOpen: false,
  isHintVisible: false,
  chatsDataCache: new Map<DocID, ChatWithLastAttendance>(),

  enterSelectionMode: () =>
    set((state) => ({
      ...state,
      mode: "selecting",
      isHintVisible: true,
    })),

  exitSelectionMode: () =>
    set((state) => ({
      ...state,
      mode: "off",
      selectedChatIds: new Set<DocID>(),
      lastClickedChatId: null,
      isConfirmModalOpen: false,
      isTagModalOpen: false,
      isHintVisible: false,
      chatsDataCache: new Map<DocID, ChatWithLastAttendance>(),
    })),

  toggleChat: (chatId) =>
    set((state) => {
      const next = new Set(state.selectedChatIds);
      if (next.has(chatId)) {
        next.delete(chatId);
      } else {
        if (next.size >= BULK_SELECTION_CAP) return state;
        next.add(chatId);
      }
      return {
        ...state,
        mode: state.mode === "off" ? "selecting" : state.mode,
        selectedChatIds: next,
        lastClickedChatId: chatId,
        isHintVisible: false,
      };
    }),

  selectRange: (visibleIds, fromId, toId) =>
    set((state) => {
      const fromIdx = visibleIds.indexOf(fromId);
      const toIdx = visibleIds.indexOf(toId);
      if (fromIdx === -1 || toIdx === -1) return state;
      const [start, end] =
        fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
      const range = visibleIds.slice(start, end + 1);
      const next = new Set(state.selectedChatIds);
      for (const id of range) {
        if (next.size >= BULK_SELECTION_CAP) break;
        next.add(id);
      }
      return {
        ...state,
        mode: state.mode === "off" ? "selecting" : state.mode,
        selectedChatIds: next,
        lastClickedChatId: toId,
        isHintVisible: false,
      };
    }),

  setLastClickedChatId: (id) =>
    set((state) => ({
      ...state,
      lastClickedChatId: id,
    })),

  selectAllVisible: (chatIds) =>
    set((state) => {
      const allAlreadySelected =
        state.mode !== "off" &&
        chatIds.length > 0 &&
        chatIds.every((id) => state.selectedChatIds.has(id));

      if (allAlreadySelected) {
        const next = new Set(state.selectedChatIds);
        chatIds.forEach((id) => next.delete(id));
        return {
          ...state,
          mode: "selecting",
          selectedChatIds: next,
          isHintVisible: false,
        };
      }

      const next = new Set(state.selectedChatIds);
      for (const id of chatIds) {
        if (next.size >= BULK_SELECTION_CAP) break;
        next.add(id);
      }
      return {
        ...state,
        mode: "selecting",
        selectedChatIds: next,
        isHintVisible: false,
      };
    }),

  selectAllInTab: () =>
    set((state) => {
      if (state.tabTotal === null) return state;
      return {
        ...state,
        mode: "all_in_tab",
        isHintVisible: false,
      };
    }),

  selectFailedChats: (chatIds) =>
    set((state) => ({
      ...state,
      mode: "selecting",
      selectedChatIds: new Set(chatIds),
    })),

  clearSelection: () =>
    set((state) => ({
      ...state,
      selectedChatIds: new Set<DocID>(),
      mode: state.mode === "all_in_tab" ? "selecting" : state.mode,
    })),

  setTabTotal: (total) =>
    set((state) => ({
      ...state,
      tabTotal: total,
    })),

  decrementTabTotalBy: (delta) =>
    set((state) => ({
      ...state,
      tabTotal:
        state.tabTotal === null ? null : Math.max(0, state.tabTotal - delta),
    })),

  openConfirmModal: () =>
    set((state) => ({ ...state, isConfirmModalOpen: true })),

  closeConfirmModal: () =>
    set((state) => ({ ...state, isConfirmModalOpen: false })),

  openTagModal: () => set((state) => ({ ...state, isTagModalOpen: true })),

  closeTagModal: () => set((state) => ({ ...state, isTagModalOpen: false })),

  dismissHint: () => set((state) => ({ ...state, isHintVisible: false })),

  cacheChats: (chats) =>
    set((state) => {
      if (chats.length === 0) return state;
      const next = new Map(state.chatsDataCache);
      let changed = false;
      for (const c of chats) {
        if (!c.documentId) continue;
        const existing = next.get(c.documentId);
        if (existing !== c) {
          next.set(c.documentId, c);
          changed = true;
        }
      }
      if (!changed) return state;
      return { ...state, chatsDataCache: next };
    }),

  onFilterChanged: () =>
    set((state) => {
      if (state.mode === "all_in_tab") {
        return {
          ...state,
          mode: "off",
          selectedChatIds: new Set<DocID>(),
        };
      }
      return state;
    }),
}));
